import React, { useState, useEffect, useRef } from 'react';
import { useReactFlow } from '@xyflow/react';
import { useApi } from '../../../../context/ApiContext/ApiContext';
// import { ArrowRight } from 'lucide-react'

// Configurable timeout settings
const FETCH_DELAY = 2000; // Initial delay before first fetch
const RETRY_DELAY = 4000; // 4 seconds between retries
const MAX_RETRIES = 11; // Maximum number of retry attempts
const FADE_DURATION_MS = 1000; // 1 second fade in duration

const SmartplayManifestInfo = ({ id, data }) => {
  // Component state
  const [streamData, setStreamData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isVisible, setIsVisible] = useState(false);
  
  // Refs for timeouts
  const attemptCountRef = useRef(0);
  const timeoutRef = useRef(null);
  const retryTimeoutRef = useRef(null);
  
  // Context hooks
  const { getNode } = useReactFlow();
  const { state, updateSharedParams, appendUplynkJobState } = useApi();
  
  const node = getNode(id);
  const measurements = { "width": node.width, "height": node.height };

  // Fetch JSON data with retry mechanism
  useEffect(() => {
    setStreamData(null);
    setIsVisible(false);
    setIsLoading(false);
    setError(null);
    attemptCountRef.current = 0;
    timeoutRef.current = null;
    retryTimeoutRef.current = null;
    
    // If no URL in state, do nothing
    if (!state.sharedParams?.playerURL) return;
    
    // Clear any existing timeouts
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    if (retryTimeoutRef.current) clearTimeout(retryTimeoutRef.current);
    
    const fetchStreamData = async () => {
      setIsLoading(true);
      // Log "TESTING" URL
      console.log(`URL Check ${attemptCountRef.current + 1}/${MAX_RETRIES + 1}`);
      appendUplynkJobState({
        state: 'waiting',
        timestamp: new Date(),
        title: `URL Check ${attemptCountRef.current + 1}/${MAX_RETRIES + 1}`,
        progress: 0,
        extractedData: {}
      });

      try {

        // Replace m3u8 with json in the URL
        const jsonUrl = state.sharedParams.playerURL.replace('.m3u8', '.json');
        console.log(`Fetching stream data (attempt ${attemptCountRef.current + 1}/${MAX_RETRIES + 1}):`, jsonUrl);
        
        const response = await fetch(jsonUrl);
        
        if (!response.ok) {
          throw new Error(`Failed to fetch: ${response.status}`);
        }
        
        const data = await response.json();
        console.log('Stream data received:', data);
        setStreamData(data);
        setError(null);

        // Log Sending OK to Player
        setTimeout(() => {
          appendUplynkJobState({
            state: 'playing',
            timestamp: new Date(),
            title: "URL > Player..",
            progress: state.sharedParams.playerURL,
            extractedData: {}
          });
          updateSharedParams({ isPlayerURLReady: true });
          setIsVisible(true);
        }, RETRY_DELAY); // Use the same delay as retry for consistency

        
        // Set timeout to show component after data is loaded
        // timeoutRef.current = setTimeout(() => {
        //   setIsVisible(true);
        // }, 0);
        
      } catch (err) {
        console.error(`Error fetching stream data (attempt ${attemptCountRef.current + 1}/${MAX_RETRIES + 1}):`, err);
        setError(err.message || 'Error fetching stream data');

        // Retry logic
        const nextAttempt = attemptCountRef.current + 1;
        // setAttemptCount(nextAttempt);
        attemptCountRef.current = nextAttempt;
        
        if (nextAttempt <= MAX_RETRIES) {
          console.log(`Retrying in ${RETRY_DELAY/1000} seconds...`);

          retryTimeoutRef.current = setTimeout(fetchStreamData, RETRY_DELAY);
        } else {
          console.log('Maximum retry attempts reached. Giving up.');
        }
      } finally {
        setIsLoading(false);
      }
    };
    
    // Initial fetch after delay
    const initialFetchTimeout = setTimeout(() => {
      fetchStreamData();
    }, FETCH_DELAY);
    
    // Cleanup function
    return () => {
      clearTimeout(initialFetchTimeout);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (retryTimeoutRef.current) clearTimeout(retryTimeoutRef.current);
    };
  }, [state.sharedParams?.playerURL]);

  // Don't render anything if we have no data and finished trying
  if (!streamData && !isLoading && attemptCountRef.current > MAX_RETRIES) {
    return null;
  }

  return (
    <div 
      className={`relative bg-[#000000bb] text-white flex flex-col pb-1.5 justify-center nowheel nopan module-border-pulse`}
      style={{
        borderWidth: data.style.borderWidth,
        borderColor: data.style.borderColor,
        borderRadius: "20px",
        width: measurements.width * 1,
        minHeight: `${measurements.height}px`,
        maxHeight: `${measurements.height * 1.21}px`,
        opacity: isVisible ? 1 : 0,
        transition: `opacity ${FADE_DURATION_MS}ms ease-in-out`,
        visibility: streamData ? 'visible' : 'hidden',
      }}
    >
      {/* <div className="absolute top-1/2 mt-2 -right-7 animate-pulse font-bold"><ArrowRight size={18}/></div> */}
      {isLoading && (
        <div className="flex items-center justify-center text-xs">
          Loading...
        </div>
      )}
      
      {error && (
        <div className="text-red-400 text-xs overflow-hidden">
          Error: {error}
        </div>
      )}
      
      <header className="pt-1.5 pb-1 text-[0.9em] font-semibold text-gray-400 text-center">Smartplay Manifest</header>
      <div className="flex flex-col flex-grow overflow-hidden pr-0.5">
        {streamData && !isLoading && !error && (
          <div className="overflow-auto custom-scrollbar px-1">
            <table className="w-full text-[0.8em]">
              {/* <thead className="">
                <tr className="text-gray-400">
                  <th className="pb-0 pt-0 text-sm text-center" colSpan={2}>Smartplay Manifest</th>
                </tr>
              </thead> */}
              <tbody>
                {streamData.rays
                  ?.slice() // Create a copy to avoid mutating the original array
                  .sort((a, b) => b.bps - a.bps) // Sort from highest to lowest
                  .map((ray, index) => (
                    <tr key={index} className="border-t border-gray-700">
                      <td className="text-left py-1 text-[0.6rem]">{ray.dims}</td>
                      <td className="text-right py-1 text-[0.6rem]">{Math.round(ray.bps / 1000)} <span className="text-gray-300">kb/s</span></td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      
      {!streamData && !isLoading && !error && (
        <div className="flex items-center justify-center h-[100px] text-xs text-gray-400">
          No stream data available
        </div>
      )}
    </div>
  );
};

export default SmartplayManifestInfo;